import { data, useRouteLoaderData } from "react-router";
import { SITE_NAME, SITE_URL } from "~/lib/seo";
import { slugify } from "~/lib/slug";
import type { CompanyProfile } from "~/types/company";

/** Route id of the company layout route — children read the shared profile from it. */
export const COMPANY_LAYOUT_ROUTE_ID = "routes/company/company-layout";

/** Extracts the IČO from a `{ico}-{slug}` URL param. Throws 404 when malformed. */
export function parseIcoSlug(icoSlug: string): string {
	const match = icoSlug.match(/^(\d{1,8})/);
	if (!match) throw data(null, { status: 404 });
	return match[1];
}

export function companySlug(name: string): string {
	return slugify(name);
}

/** Provides a usable label when an upstream company record has no legal name. */
export function companyDisplayName(company: Pick<CompanyProfile, "ico" | "name">): string {
	return company.name.trim() || `Firma s IČO ${company.ico}`;
}

/** App-relative path of a company profile: `/firma/{ico}-{slug}`. */
export function companyPath(ico: string, name: string): string {
	const slug = companySlug(name);
	return slug ? `/firma/${ico}-${slug}` : `/firma/${ico}`;
}

/** Canonical absolute URL of a company section page (no section = Přehled). */
export function companyCanonicalUrl(
	company: Pick<CompanyProfile, "ico" | "name">,
	section?: string,
): string {
	const base = `${SITE_URL}/firma/${company.ico}-${companySlug(company.name)}`;
	return section ? `${base}/${section}` : base;
}

/** Absolute URL and accessible description for a company's generated social preview image. */
export function companySocialMeta(company: Pick<CompanyProfile, "ico" | "name">) {
	const slug = companySlug(company.name);
	const imagePath = slug ? `${company.ico}-${slug}` : company.ico;

	return {
		ogImage: `${SITE_URL}/og/firma/${imagePath}/image.png`,
		ogImageAlt: `Základní údaje o firmě ${company.name} na FirmIndexu`,
	};
}

/**
 * Reads the company profile loaded by the company layout route. Only valid
 * inside child routes of `routes/company/company-layout`.
 */
export function useCompanyProfile(): CompanyProfile {
	const loaderData = useRouteLoaderData(COMPANY_LAYOUT_ROUTE_ID) as
		| { company: CompanyProfile }
		| undefined;
	if (!loaderData) {
		throw new Error("useCompanyProfile must be used within the company layout route");
	}
	return loaderData.company;
}

interface RouteMatchLike {
	id: string;
	data: unknown;
}

/**
 * Finds the company profile in `matches` (for child-route `meta()` functions,
 * which cannot use hooks). Returns undefined when the layout loader errored.
 */
export function companyFromMatches(
	matches: ReadonlyArray<RouteMatchLike | undefined>,
): CompanyProfile | undefined {
	const match = matches.find((m) => m?.id === COMPANY_LAYOUT_ROUTE_ID);
	const loaderData = match?.data as { company?: CompanyProfile } | undefined;
	return loaderData?.company;
}

/** Meta tags for the not-found / error case (parity with the old monolith route). */
export function companyNotFoundMeta() {
	return [
		{ title: `Firma nenalezena | ${SITE_NAME}` },
		{ name: "robots", content: "noindex, nofollow" },
	];
}

/**
 * Fetches a section endpoint for a company-profile child route. Returns null
 * on API/network errors so the section degrades to an in-tab error state
 * instead of replacing the whole profile page with the root error boundary.
 * Throws 404 only when the resource itself is missing (company not found).
 */
export async function fetchCompanySection<T>(
	url: string,
	headers?: Record<string, string>,
): Promise<T | null> {
	let res: Response;
	try {
		res = await fetch(url, { headers });
	} catch {
		return null;
	}
	if (res.status === 404) throw data(null, { status: 404 });
	if (!res.ok) return null;
	try {
		return (await res.json()) as T;
	} catch {
		return null;
	}
}
